import React from 'react';
import { Query, Mutation } from 'react-apollo';
import gql from 'graphql-tag';
import { CircularProgress, Box } from '@material-ui/core';
import TriggerBuilder from '../../components/EmailPlatform/TriggerBuilder';
import { withSnackbar } from 'notistack';
import { withRouter } from 'react-router-dom';

const GET_TRIGGER = gql`
  query GetEmailTrigger($id: ID!) {
    email_trigger(id: $id) {
      id
      name
      event_type
      action_type
      active
      requires_approval
      condition_json
      email_template {
        id
        name
      }
      sequence {
        id
        name
      }
    }
  }
`;

const GET_TEMPLATES_AND_SEQUENCES = gql`
  query GetTemplatesAndSequences {
    emailTemplates {
      id
      name
      category
    }
    emailSequences {
      id
      name
      active
    }
  }
`;

const CREATE_TRIGGER = gql`
  mutation CreateEmailTrigger(
    $name: String!
    $event_type: String!
    $action_type: String!
    $condition_json: String
    $template_id: ID
    $sequence_id: ID
    $active: Boolean
    $requires_approval: Boolean
  ) {
    create_email_trigger(
      name: $name
      event_type: $event_type
      action_type: $action_type
      condition_json: $condition_json
      template_id: $template_id
      sequence_id: $sequence_id
      active: $active
      requires_approval: $requires_approval
    ) {
      email_trigger {
        id
        name
      }
      errors
    }
  }
`;

const UPDATE_TRIGGER = gql`
  mutation UpdateEmailTrigger(
    $id: ID!
    $name: String
    $event_type: String
    $action_type: String
    $condition_json: String
    $template_id: ID
    $sequence_id: ID
    $active: Boolean
    $requires_approval: Boolean
  ) {
    update_email_trigger(
      id: $id
      name: $name
      event_type: $event_type
      action_type: $action_type
      condition_json: $condition_json
      template_id: $template_id
      sequence_id: $sequence_id
      active: $active
      requires_approval: $requires_approval
    ) {
      email_trigger {
        id
        name
      }
      errors
    }
  }
`;

class TriggerBuilderContainer extends React.Component {
  handleSave = (createTrigger, updateTrigger) => (triggerData) => {
    const mutation = triggerData.id ? updateTrigger : createTrigger;
    const variables = {
      name: triggerData.name,
      event_type: triggerData.eventType,
      action_type: triggerData.actionType,
      active: triggerData.active,
      requires_approval: triggerData.requiresApproval,
      condition_json: triggerData.conditionJson,
    };

    if (triggerData.actionType === 'send_email') {
      variables.template_id = triggerData.templateId;
    } else {
      variables.sequence_id = triggerData.sequenceId;
    }

    if (triggerData.id) {
      variables.id = triggerData.id;
    }

    mutation({
      variables,
    })
      .then(({ data }) => {
        const result = triggerData.id
          ? data.update_email_trigger
          : data.create_email_trigger;

        if (result.errors && result.errors.length > 0) {
          this.props.enqueueSnackbar(`Error: ${result.errors.join(', ')}`, {
            variant: 'error',
          });
        } else {
          this.props.enqueueSnackbar(
            triggerData.id ? 'Trigger updated successfully' : 'Trigger created successfully',
            { variant: 'success' }
          );
          this.props.history.push('/admin/email_triggers');
        }
      })
      .catch(error => {
        this.props.enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
      });
  };

  handleCancel = () => {
    this.props.history.push('/admin/email_triggers');
  };

  render() {
    const triggerId = this.props.match.params.id;
    const isEdit = !!triggerId;

    return (
      <Query query={GET_TEMPLATES_AND_SEQUENCES}>
        {({ loading: loadingData, error: dataError, data: dataData }) => {
          if (loadingData) {
            return (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
              </Box>
            );
          }

          if (dataError) {
            return <div>Error loading data: {dataError.message}</div>;
          }

          const templates = dataData.emailTemplates || [];
          const sequences = dataData.emailSequences || [];

          if (isEdit) {
            return (
              <Query query={GET_TRIGGER} variables={{ id: triggerId }}>
                {({ loading: loadingTrigger, error: triggerError, data: triggerData }) => {
                  if (loadingTrigger) {
                    return (
                      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                        <CircularProgress />
                      </Box>
                    );
                  }

                  if (triggerError) {
                    return <div>Error loading trigger: {triggerError.message}</div>;
                  }

                  // Map snake_case GraphQL fields to the camelCase the legacy
                  // TriggerBuilder component reads.
                  const rawTrigger = triggerData.email_trigger;
                  const trigger = rawTrigger ? {
                    ...rawTrigger,
                    eventType:        rawTrigger.event_type,
                    actionType:       rawTrigger.action_type,
                    requiresApproval: rawTrigger.requires_approval,
                    conditionJson:    rawTrigger.condition_json,
                    emailTemplate:    rawTrigger.email_template,
                    emailSequence:    rawTrigger.sequence,
                  } : null;

                  return (
                    <Mutation mutation={CREATE_TRIGGER}>
                      {(createTrigger) => (
                        <Mutation mutation={UPDATE_TRIGGER}>
                          {(updateTrigger) => (
                            <TriggerBuilder
                              trigger={trigger}
                              templates={templates}
                              sequences={sequences}
                              onSave={this.handleSave(createTrigger, updateTrigger)}
                              onCancel={this.handleCancel}
                            />
                          )}
                        </Mutation>
                      )}
                    </Mutation>
                  );
                }}
              </Query>
            );
          }

          return (
            <Mutation mutation={CREATE_TRIGGER}>
              {(createTrigger) => (
                <Mutation mutation={UPDATE_TRIGGER}>
                  {(updateTrigger) => (
                    <TriggerBuilder
                      trigger={null}
                      templates={templates}
                      sequences={sequences}
                      onSave={this.handleSave(createTrigger, updateTrigger)}
                      onCancel={this.handleCancel}
                    />
                  )}
                </Mutation>
              )}
            </Mutation>
          );
        }}
      </Query>
    );
  }
}

export default withRouter(withSnackbar(TriggerBuilderContainer));

